// context/CartContext.tsx
'use client';

import React, {
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useModal } from './ModalContext';

export type CartItem = {
	id: string;
	name: string;
	price: number;
	qty: number;
	image?: string;
	storeId?: string;
	storeName?: string;
	category?: string;
};

type CartContextValue = {
	items: CartItem[];
	totalItems: number;
	subtotal: number;
	addItem: (
		item: Omit<CartItem, 'qty'>,
		qty?: number,
	) => void;
	removeItem: (id: string) => void;
	setQty: (id: string, qty: number) => void;
	clear: () => void;
	open: boolean;
	setOpen: (v: boolean) => void;
};

const CartContext = createContext<
	CartContextValue | undefined
>(undefined);

const STORAGE_KEY = 'yunimall_cart_v1'; 

export function CartProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const { user } = useAuth(); // Get user from auth context
	const { showAlert } = useModal();
	const [items, setItems] = useState<CartItem[]>([]);
	const [open, setOpen] = useState(false);

	const isInitialized = React.useRef(false); // Ref to track if we've loaded from storage

	// Load from storage
	useEffect(() => {
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (Array.isArray(parsed)) {
					// Normalize data to ensure compatibility
					const normalized = parsed.map((it) => ({
						id: it.id,
						name: it.name,
						price: Number(it.price || 0),
						qty: Number(it.qty || 1),
						image: it.image || '',
						storeId: it.storeId,
						storeName: it.storeName,
						category: it.category,
					}));
					setItems(normalized);
				}
			}
		} catch (err) {
			console.warn(
				'Cart: failed to load from storage',
				err,
			);
		} finally {
			isInitialized.current = true; // Mark as initialized after attempting load
		}
	}, []);

	// Save to storage
	useEffect(() => {
		// Prevent saving initial empty state before we've verified storage
		if (!isInitialized.current) return;
		try {
			localStorage.setItem(
				STORAGE_KEY,
				JSON.stringify(items),
			);
		} catch (err) {
			console.warn('Cart: failed to save to storage', err);
		}
	}, [items]);

	const addItem = (
		item: Omit<CartItem, 'qty'>,
		qty = 1,
	) => {
		// Use optional chaining just in case user is null (though auth context usually handles this)
		if (user?.isDisabled) {
			showAlert({ title: 'Account Disabled', message: 'Your account is disabled. You cannot make purchases.', type: 'danger' });
			return;
		}

		setItems((prev) => {
			const idx = prev.findIndex((p) => p.id === item.id);
			if (idx >= 0) {
				const copy = [...prev];
				// Update existing item, preserving existing info but updating store/category if provided
				copy[idx] = {
					...copy[idx],
					qty: copy[idx].qty + qty,
					storeId: item.storeId ?? copy[idx].storeId,
					storeName: item.storeName ?? copy[idx].storeName,
					category: item.category ?? copy[idx].category,
				};
				return copy;
			}
			return [...prev, { ...item, qty }];
		});
		setOpen(true);
	};

	const removeItem = (id: string) => {
		setItems((prev) => prev.filter((p) => p.id !== id));
	};

	const setQty = (id: string, qty: number) => {
		if (qty <= 0) {
			removeItem(id);
			return;
		}
		setItems((prev) =>
			prev.map((p) => (p.id === id ? { ...p, qty } : p)),
		);
	};

	const clear = () => {
		setItems([]);
		// Force direct storage update to avoid race conditions with effects
		try {
			localStorage.removeItem(STORAGE_KEY);
		} catch { }
	};

	const totalItems = items.reduce((s, it) => s + it.qty, 0);
	const subtotal = items.reduce(
		(s, it) => s + it.qty * it.price,
		0,
	);

	const value: CartContextValue = {
		items,
		totalItems,
		subtotal,
		addItem,
		removeItem,
		setQty,
		clear,
		open,
		setOpen,
	};

	return (
		<CartContext.Provider value={value}>
			{children}
		</CartContext.Provider>
	);
}

export function useCart() {
	const ctx = useContext(CartContext);
	if (!ctx) {
		throw new Error(
			'useCart must be used inside CartProvider',
		);
	}
	return ctx;
}
